import {Routes} from '@angular/router';
import {loggedInGuard} from './core/guards/logged-in.guard';
import {notLoggedInGuard} from './core/guards/not-logged-in.guard';
import {UserRole} from 'patja-api';
import {ChallengeGuard} from './core/guards/temporary-token.guard';
import {unsavedChangesGuard} from './core/guards/unsaved-changes.guard';
import {rolesGuard} from './core/guards/roles.guard';

//eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
export enum RoutePathNames {
  Auth = 'auth',
  Login = 'login',
  ResetPassword = 'reset-password',
  Verify = 'verify',
  Verifying = 'verify-account',
  Home = '',
  Dashboard = 'dashboard',
  Users = 'users',
  AccountingOffices = 'accounting-offices',
  Companies = 'companies',
  Settings = 'settings',
  ResetPasswordLinkExpired = 'link-expired',
  CheckKey = 'check',
  SessionExpired = 'expired',
  TwoFactor = 'two-factor',
  EnableTwoFactor = 'enable-two-factor',
  ChooseMethod = 'choose-method',
  GoogleAuthenticator = 'google-auth',
  VerifyGoogleAuthenticator = 'verify-google-auth',
  VerifyBackupCode = 'verify-backup-code',
  TwoFactorOptions = 'two-factor-options',
  BackupCodes = 'backup-codes',
  Verified = 'verified-account',
  TermsOfUse = 'terms-of-use',
  PrivacyPolicy = 'privacy-policy',
  SetPassword = 'set-password',
  AiConfiguration = 'ai-configuration',
  FileTemplates = 'file-templates',
  Accounts = 'accounts',
  ProductRegister = 'product-register',
  Chats = 'chats',
  AdminActivity = 'admin-activity',
  AdminReceiptView = 'admin-receipt-view',
  ConfirmEmailChange = 'confirm-email-change',
}

export enum RoutePaths {
  Login = `${RoutePathNames.Auth}/${RoutePathNames.Login}`,
  ResetPassword = `${RoutePathNames.Auth}/${RoutePathNames.ResetPassword}`,
  Verify = `${RoutePathNames.Verify}`,
  Home = `${RoutePathNames.Home}`,
  Dashboard = `${RoutePathNames.Home}/${RoutePathNames.Dashboard}`,
  Users = `${RoutePathNames.Home}/${RoutePathNames.Users}`,
  NewUser = `${RoutePathNames.Users}/new`,
  AccountingOffices = `${RoutePathNames.Home}/${RoutePathNames.AccountingOffices}`,
  Companies = `${RoutePathNames.Home}/${RoutePathNames.Companies}`,
  Settings = `${RoutePathNames.Home}/${RoutePathNames.Settings}`,
  SessionExpired = `${RoutePathNames.Auth}/${RoutePathNames.SessionExpired}`,
  EnableTwoFactor = `${RoutePathNames.TwoFactor}/${RoutePathNames.EnableTwoFactor}`,
  TwoFactorOptions = `${RoutePathNames.TwoFactor}/${RoutePathNames.TwoFactorOptions}`,
  VerifyBackupCode = `${RoutePathNames.TwoFactor}/${RoutePathNames.VerifyBackupCode}`,
  TwoFactorBackupCodes = `${RoutePathNames.TwoFactor}/${RoutePathNames.BackupCodes}`,
  ChooseMethod = `${RoutePathNames.TwoFactor}/${RoutePathNames.ChooseMethod}`,
  GoogleAuthenticator = `${RoutePathNames.TwoFactor}/${RoutePathNames.GoogleAuthenticator}`,
  VerifyGoogleAuthenticator = `${RoutePathNames.TwoFactor}/${RoutePathNames.VerifyGoogleAuthenticator}`,
  AiConfiguration = `${RoutePathNames.Home}/${RoutePathNames.AiConfiguration}`,
  FileTemplates = `${RoutePathNames.Home}/${RoutePathNames.FileTemplates}`,
  Chats = `${RoutePathNames.Home}/${RoutePathNames.Chats}`,
  AdminActivity = `${RoutePathNames.Home}/${RoutePathNames.AdminActivity}`,
  AdminReceiptView = `${RoutePathNames.Home}/${RoutePathNames.AdminActivity}/receipts`,
}

export const routes: Routes = [
  {
    path: RoutePathNames.Auth,
    loadComponent: () => import('./components/auth/auth.component').then(c => c.AuthComponent),
    canActivate: [notLoggedInGuard],
    children: [
      {
        path: '',
        redirectTo: RoutePathNames.Login,
        pathMatch: 'full',
      },
      {
        path: RoutePathNames.Login,
        loadComponent: () =>
          import('./components/auth/login/login.component').then(c => c.LoginComponent),
      },
      {
        path: RoutePathNames.ResetPassword,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./components/auth/reset-password/reset-password.component').then(
                c => c.ResetPasswordComponent,
              ),
          },
          {
            path: 'new',
            loadComponent: () =>
              import(
                './components/auth/reset-password/set-new-password/set-new-password.component'
              ).then(c => c.SetNewPasswordComponent),
          },
          {
            path: RoutePathNames.ResetPasswordLinkExpired,
            loadComponent: () =>
              import('./components/auth/reset-password/link-expired/link-expired.component').then(
                c => c.LinkExpiredComponent,
              ),
          },
          {
            path: RoutePathNames.CheckKey,
            loadComponent: () =>
              import('./components/auth/reset-password/check-key/check-key.component').then(
                c => c.CheckKeyComponent,
              ),
          },
        ],
      },
      {
        path: RoutePathNames.SetPassword,
        loadComponent: () =>
          import('./components/auth/set-password/set-password.component').then(
            c => c.SetPasswordComponent,
          ),
      },
      {
        path: RoutePathNames.SessionExpired,
        loadComponent: () =>
          import('./components/auth/session-expired/session-expired.component').then(
            c => c.SessionExpiredComponent,
          ),
      },
    ],
  },
  {
    path: RoutePathNames.TwoFactor,
    loadComponent: () =>
      import('../../../patja-ui/src/lib/components/two-factor-auth/two-factor-auth.component').then(
        c => c.TwoFactorAuthComponent,
      ),
    children: [
      {
        path: '',
        redirectTo: RoutePathNames.EnableTwoFactor,
        pathMatch: 'full',
      },
      {
        path: RoutePathNames.EnableTwoFactor,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/enable-two-factor/enable-two-factor.component'
          ).then(c => c.EnableTwoFactorComponent),
        canActivate: [loggedInGuard],
      },
      {
        path: RoutePathNames.ChooseMethod,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/choose-auth-method/choose-auth-method.component'
          ).then(c => c.ChooseAuthMethodComponent),
        canActivate: [loggedInGuard],
      },
      {
        path: RoutePathNames.GoogleAuthenticator,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/google-auth/google-auth.component'
          ).then(c => c.GoogleAuthComponent),
        canActivate: [loggedInGuard],
      },
      {
        path: RoutePathNames.VerifyGoogleAuthenticator,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/verify-google-auth/verify-google-auth.component'
          ).then(c => c.VerifyGoogleAuthComponent),
        // providers: [{provide: AuthService}, {provide: NavigationService}],
      },
      {
        path: RoutePathNames.BackupCodes,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/backup-codes/backup-codes.component'
          ).then(c => c.BackupCodesComponent),
        canActivate: [loggedInGuard],
      },
      {
        path: RoutePathNames.TwoFactorOptions,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/two-factor-options/two-factor-options.component'
          ).then(c => c.TwoFactorOptionsComponent),
        canActivate: [ChallengeGuard],
      },
      {
        path: RoutePathNames.VerifyBackupCode,
        loadComponent: () =>
          import(
            '../../../patja-ui/src/lib/components/two-factor-auth/two-factor-options/verify-backup-code/verify-backup-code.component'
          ).then(c => c.VerifyBackupCodeComponent),
        canActivate: [ChallengeGuard],
      },
    ],
  },
  {
    path: RoutePathNames.ConfirmEmailChange,
    loadComponent: () =>
      import('./components/auth/confirm-email-change/confirm-email-change.component').then(
        c => c.ConfirmEmailChangeComponent,
      ),
  },
  {
    path: RoutePathNames.TermsOfUse,
    loadComponent: () =>
      import('./components/terms-of-use/terms-of-use.component').then(c => c.TermsOfUseComponent),
  },
  {
    path: RoutePathNames.PrivacyPolicy,
    loadComponent: () =>
      import('./components/privacy-policy/privacy-policy.component').then(
        c => c.PrivacyPolicyComponent,
      ),
  },
  {
    path: RoutePathNames.Home,
    loadComponent: () =>
      import('./components/layout/layout.component').then(c => c.LayoutComponent),
    canActivate: [loggedInGuard],
    children: [
      {
        path: '',
        redirectTo: RoutePathNames.Dashboard,
        pathMatch: 'full',
      },
      {
        path: RoutePathNames.Dashboard,
        loadComponent: () =>
          import('./components/layout/pages/dashboard/dashboard.component').then(
            c => c.DashboardComponent,
          ),
      },
      {
        path: 'tilastot-ja-data',
        redirectTo: RoutePathNames.Dashboard,
        pathMatch: 'full',
      },
      {
        path: RoutePathNames.Users,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./components/layout/pages/users/users.component').then(c => c.UsersComponent),
          },
          {
            path: 'new',
            loadComponent: () =>
              import('./components/layout/pages/users/new-user/new-user.component').then(
                c => c.NewUserComponent,
              ),
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: ':userId',
            loadComponent: () =>
              import('./components/layout/pages/users/view-user/view-user.component').then(
                c => c.ViewUserComponent,
              ),
          },
          {
            path: `:userId/edit`,
            loadComponent: () =>
              import('./components/layout/pages/users/edit-user/edit-user.component').then(
                c => c.EditUserComponent,
              ),
            canDeactivate: [unsavedChangesGuard],
          },
        ],
      },
      {
        path: RoutePathNames.AccountingOffices,
        canActivate: [loggedInGuard],
        data: {
          allowedRoles: [UserRole.SuperAdmin, UserRole.Helpdesk, UserRole.AccountingOfficeAdmin],
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './components/layout/pages/accounting-offices/accounting-offices.component'
              ).then(c => c.AccountingOfficesComponent),
          },
          {
            path: 'new',
            loadComponent: () =>
              import(
                './components/layout/pages/accounting-offices/create-accounting-office/create-accounting-office.component'
              ).then(c => c.CreateAccountingOfficeComponent),
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: ':accountingOfficeId',
            loadComponent: () =>
              import(
                './components/layout/pages/accounting-offices/view-accounting-office/view-accounting-office.component'
              ).then(c => c.ViewAccountingOfficeComponent),
          },
          {
            path: ':accountingOfficeId/edit',
            loadComponent: () =>
              import(
                './components/layout/pages/accounting-offices/edit-accounting-office/edit-accounting-office.component'
              ).then(c => c.EditAccountingOfficeComponent),
            canDeactivate: [unsavedChangesGuard],
          },
        ],
      },
      {
        path: RoutePathNames.AiConfiguration,
        loadComponent: () =>
          import('./components/layout/pages/ai-configuration/ai-configuration.component').then(
            c => c.AiConfigurationComponent,
          ),
        data: {allowedRoles: [UserRole.SuperAdmin]},
      },
      {
        path: RoutePathNames.Companies,
        canActivate: [loggedInGuard],
        data: {
          allowedRoles: [
            UserRole.SuperAdmin,
            UserRole.Helpdesk,
            UserRole.AccountingOfficeAdmin,
            UserRole.CompanyAdmin,
          ],
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./components/layout/pages/companies/companies.component').then(
                c => c.CompaniesComponent,
              ),
          },
          {
            path: 'new',
            loadComponent: () =>
              import(
                './components/layout/pages/companies/create-company/create-company.component'
              ).then(c => c.CreateCompanyComponent),
            canActivate: [rolesGuard],
            data: {allowedRoles: [UserRole.SuperAdmin, UserRole.Helpdesk]},
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: ':companyId',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './components/layout/pages/companies/view-company/view-company.component'
                  ).then(c => c.ViewCompanyComponent),
              },
              {
                path: `${RoutePathNames.Accounts}`,
                canActivate: [rolesGuard],
                data: {allowedRoles: [UserRole.SuperAdmin, UserRole.CompanyAdmin]},
                children: [
                  {
                    path: '',
                    redirectTo: 'new',
                    pathMatch: 'full',
                  },
                  {
                    path: 'new',
                    loadComponent: () =>
                      import(
                        './components/layout/pages/companies/view-company/chart-accounts/new-account/new-account.component'
                      ).then(c => c.NewAccountComponent),
                  },
                  {
                    path: ':accountId/edit',
                    loadComponent: () =>
                      import(
                        './components/layout/pages/companies/view-company/chart-accounts/edit-account/edit-account.component'
                      ).then(c => c.EditAccountComponent),
                  },
                ],
              },
              {
                path: `${RoutePathNames.ProductRegister}`,
                canActivate: [rolesGuard],
                data: {allowedRoles: [UserRole.SuperAdmin, UserRole.CompanyAdmin]},
                children: [
                  {
                    path: '',
                    redirectTo: 'new',
                    pathMatch: 'full',
                  },
                  {
                    path: 'new',
                    loadComponent: () =>
                      import(
                        './components/layout/pages/companies/view-company/product-register/new-product/new-product.component'
                      ).then(c => c.NewProductComponent),
                  },
                  {
                    path: ':productId/edit',
                    loadComponent: () =>
                      import(
                        './components/layout/pages/companies/view-company/product-register/edit-product/edit-product.component'
                      ).then(c => c.EditProductComponent),
                  },
                ],
              },
            ],
          },
          {
            path: ':companyId/edit',
            loadComponent: () =>
              import(
                './components/layout/pages/companies/edit-company/edit-company.component'
              ).then(c => c.EditCompanyComponent),
            canActivate: [rolesGuard],
            data: {
              allowedRoles: [UserRole.SuperAdmin, UserRole.Helpdesk, UserRole.CompanyAdmin],
            },
            canDeactivate: [unsavedChangesGuard],
          },
        ],
      },
      {
        path: RoutePathNames.FileTemplates,
        loadComponent: () =>
          import('./components/layout/pages/file-templates/file-templates.component').then(
            c => c.FileTemplatesComponent,
          ),
      },
      {
        path: RoutePathNames.Settings,
        loadComponent: () =>
          import('./components/layout/pages/settings/settings.component').then(
            c => c.SettingsComponent,
          ),
      },
      {
        path: RoutePathNames.Chats,
        loadComponent: () =>
          import('./components/layout/pages/chats/admin-chats.component').then(
            c => c.AdminChatsComponent,
          ),
        data: {
          allowedRoles: [UserRole.SuperAdmin, UserRole.AccountingOfficeAdmin],
        },
      },
      {
        path: `${RoutePathNames.AdminActivity}/receipts/:receiptId`,
        canActivate: [loggedInGuard],
        data: {allowedRoles: [UserRole.SuperAdmin, UserRole.AccountingOfficeAdmin]},
        loadComponent: () =>
          import('./components/layout/pages/admin-activity/admin-receipt-view.component').then(
            c => c.AdminReceiptViewComponent,
          ),
      },
      {
        path: `${RoutePathNames.AdminActivity}/:type`,
        canActivate: [loggedInGuard],
        data: {allowedRoles: [UserRole.SuperAdmin, UserRole.AccountingOfficeAdmin]},
        loadComponent: () =>
          import('./components/layout/pages/admin-activity/admin-activity-list.component').then(
            c => c.AdminActivityListComponent,
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: RoutePathNames.Home,
    pathMatch: 'full',
  },
];
